/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { createSlice, current, PayloadAction } from "@reduxjs/toolkit";

import { RequestStatus } from "~/constants";
import {
  DEFAULT_FILTER_INTERVAL,
  StatisticsGroupBy,
  StatisticsPlatform,
  StatisticsTab,
} from "~/features/statistics/constants";
import statisticsTabConfigMap from "~/features/statistics/statisticsTabConfigMap";
import {
  autoLoadReportAsync,
  getEmailSubscriptionAsync,
  loadSitesAsync,
  sendEmailSubscriptionAsync,
} from "~/features/statistics/thunks";
import { FilterOptions, Geo, PlatformStats, StatisticsState, TotalStats } from "~/features/statistics/types";
import { ReportType } from "~/types";
import getDateInterval from "~/utils/getDateInterval";

const { from, to } = getDateInterval(DEFAULT_FILTER_INTERVAL);

const totalStatsDefaultData: TotalStats["data"] = {
  items: [],
  summary: {
    premiumJoins: 0,
    nonPremiumJoins: 0,
    prepaidCardJoinsCount: 0,
    newPayingUsers: 0,
    usersAboveThreshold: 0,
    successfulThrustsAmount: 0,
    totalChargedBackCount: 0,
    totalChargedBack: 0,
    totalRefunded: 0,
    totalClicks: 0,
    siteId: 0,
    netRevenue: 0,
    netPayout: 0,
    uniqueLogins: 0,
  },
};

const geoReportDefaultData: Geo["data"] = {
  items: [],
  summary: {
    premiumJoins: 0,
    nonPremiumJoins: 0,
    newPayingUsers: 0,
    usersAboveThreshold: 0,
    successfulThrustsAmount: 0,
    totalChargedBackCount: 0,
    totalChargedBack: 0,
    totalRefunded: 0,
    totalClicks: 0,
    netRevenue: 0,
  },
};

const platformStatsDefaultData: PlatformStats["data"] = {
  items: [],
  summary: {
    premiumJoins: 0,
    nonPremiumJoins: 0,
    newPayingUsers: 0,
    usersAboveThreshold: 0,
    successfulThrustsAmount: 0,
    totalChargedBackCount: 0,
    totalChargedBack: 0,
    totalRefunded: 0,
    totalClicks: 0,
    netRevenue: 0,
  },
};

export const initialState: StatisticsState = {
  isReportLoading: false,
  isFullscreen: false,
  scrollToTable: false,
  openedFromReportTracker: false,
  shouldGetReport: false,
  didInit: false,
  tab: {
    activeTab: StatisticsTab.TOTAL_STATS,
    columns: {
      [StatisticsTab.TOTAL_STATS]: statisticsTabConfigMap[StatisticsTab.TOTAL_STATS].activeColumns,
      [StatisticsTab.PLATFORM_STATS]: statisticsTabConfigMap[StatisticsTab.PLATFORM_STATS].activeColumns,
      [StatisticsTab.USER_VALUE]: statisticsTabConfigMap[StatisticsTab.USER_VALUE].activeColumns,
      [StatisticsTab.GEO]: statisticsTabConfigMap[StatisticsTab.GEO].activeColumns,
      [StatisticsTab.UPSELL_STATS]: statisticsTabConfigMap[StatisticsTab.UPSELL_STATS].activeColumns,
      [StatisticsTab.PAST_PAYOUTS]: statisticsTabConfigMap[StatisticsTab.PAST_PAYOUTS].activeColumns,
      [StatisticsTab.USER_SPENDING]: statisticsTabConfigMap[StatisticsTab.USER_SPENDING].activeColumns,
      [StatisticsTab.VIDEO_SALES]: statisticsTabConfigMap[StatisticsTab.VIDEO_SALES].activeColumns,
    },
  },
  filter: {
    dev: StatisticsPlatform.ALL,
    selectedSiteIds: [],
    from,
    groupBy: StatisticsGroupBy.DATE,
    to,
    trkid: "",
  },
  emailSubscription: {
    meta: {
      initStatus: RequestStatus.IDLE,
      status: RequestStatus.IDLE,
    },
    data: null,
  },
  referrerSites: {
    meta: {
      status: RequestStatus.IDLE,
    },
    data: {
      sites: {
        byId: {},
        sortedIds: [],
        byName: {},
      },
    },
  },
  localReports: {
    totalStats: {
      meta: {
        status: RequestStatus.IDLE,
      },
      data: totalStatsDefaultData,
      pagination: {
        pageIndex: 0,
        totalPages: 0,
      },
      sorting: {
        orderByField: "siteDomain",
        orderByDirection: "ASC",
      },
      params: {
        dev: StatisticsPlatform.ALL,
        selectedSiteIds: [],
        from: "",
        groupBy: StatisticsGroupBy.DATE,
        to: "",
        trkid: "",
        lastPayouts: undefined,
      },
    },
    platformStats: {
      meta: {
        status: RequestStatus.IDLE,
      },
      data: platformStatsDefaultData,
      pagination: {
        pageIndex: 0,
        totalPages: 0,
      },
      sorting: {
        orderByField: "device",
        orderByDirection: "ASC",
      },
      params: {
        dev: StatisticsPlatform.ALL,
        selectedSiteIds: [],
        from: "",
        groupBy: StatisticsGroupBy.DATE,
        to: "",
        trkid: "",
        lastPayouts: undefined,
      },
    },
    userValue: {
      meta: {
        status: RequestStatus.IDLE,
      },
      data: {
        summary: {
          premiumJoins: 0,
          spenderCountFirstDay: 0,
          spenderCountPercentageFirstDay: 0,
          successfulThrustsAmountFirstDay: 0,
          averageAmountPerSpenderFirstDay: 0,
          spenderCountFirstWeek: 0,
          spenderCountPercentageFirstWeek: 0,
          successfulThrustsAmountFirstWeek: 0,
          averageAmountPerSpenderFirstWeek: 0,
          spenderCountFirst30Days: 0,
          spenderCountPercentageFirst30Days: 0,
          successfulThrustsAmountFirst30Days: 0,
          averageAmountPerSpenderFirst30Days: 0,
          spenderCountOver30Days: 0,
          spenderCountPercentageOver30Days: 0,
          successfulThrustsAmountOver30Days: 0,
          spenderCountTotal: 0,
          spenderCountPercentageTotal: 0,
          successfulThrustsAmountTotal: 0,
        },
        items: [],
      },
      pagination: {
        pageIndex: 0,
        totalPages: 0,
      },
      sorting: {
        orderByField: "sitedomain",
        orderByDirection: "ASC",
      },
      params: {
        dev: StatisticsPlatform.ALL,
        selectedSiteIds: [],
        from: "",
        groupBy: StatisticsGroupBy.DATE,
        to: "",
        trkid: "",
        lastPayouts: undefined,
      },
    },
    geo: {
      meta: {
        status: RequestStatus.IDLE,
      },
      data: geoReportDefaultData,
      pagination: {
        pageIndex: 0,
        totalPages: 0,
      },
      sorting: {
        orderByField: "successfulThrustsAmount",
        orderByDirection: "DESC",
      },
      params: {
        dev: StatisticsPlatform.ALL,
        selectedSiteIds: [],
        from: "",
        groupBy: StatisticsGroupBy.DATE,
        to: "",
        trkid: "",
        lastPayouts: undefined,
      },
    },
    upsellStats: {
      meta: {
        status: RequestStatus.IDLE,
      },
      data: {
        summary: {
          salesAmount: 0,
          chargebackAmount: 0,
          salesCount: 0,
          chargebackCount: 0,
          cancelCount: 0,
          netSales: 0,
          resellerPayout: 0,
          taxAmount: 0,
          chargebackAdjustment: 0,
          creditAmount: 0,
          creditAdjustment: 0,
          totalAdjustment: 0,
        },
        items: [],
      },
      pagination: {
        pageIndex: 0,
        totalPages: 0,
      },
      sorting: {
        orderByField: "siteDomain",
        orderByDirection: "ASC",
      },
      params: {
        dev: StatisticsPlatform.ALL,
        selectedSiteIds: [],
        from: "",
        groupBy: StatisticsGroupBy.DATE,
        to: "",
        trkid: "",
        lastPayouts: undefined,
      },
    },
    pastPayouts: {
      meta: {
        status: RequestStatus.IDLE,
      },
      data: {
        items: [],
        summary: [],
      },
      pagination: {
        pageIndex: 0,
        totalPages: 0,
      },
      sorting: {
        orderByField: "paymentDate",
        orderByDirection: "ASC",
      },
      params: {
        dev: StatisticsPlatform.ALL,
        selectedSiteIds: [],
        from: "",
        groupBy: StatisticsGroupBy.DATE,
        to: "",
        trkid: "",
        lastPayouts: undefined,
      },
    },
    userSpending: {
      meta: {
        status: RequestStatus.IDLE,
      },
      data: {
        items: [],
        summary: [],
      },
      pagination: {
        pageIndex: 0,
        totalPages: 0,
      },
      sorting: {
        orderByField: "userId",
        orderByDirection: "ASC",
      },
      params: {
        dev: StatisticsPlatform.ALL,
        selectedSiteIds: [],
        from: "",
        groupBy: StatisticsGroupBy.DATE,
        to: "",
        trkid: "",
        lastPayouts: undefined,
      },
    },
    videoSales: {
      meta: {
        status: RequestStatus.IDLE,
      },
      data: {
        totals: {
          videoSalesCount: 0,
          totalVideoSales: "",
          estimatedVideoSalesRevenue: "",
        },
        sites: [],
      },
      pagination: {
        pageIndex: 0,
        totalPages: 0,
      },
      sorting: {
        orderByField: "domain",
        orderByDirection: "ASC",
      },
      params: {
        dev: StatisticsPlatform.ALL,
        selectedSiteIds: [],
        from: "",
        groupBy: StatisticsGroupBy.DATE,
        to: "",
        trkid: "",
        lastPayouts: undefined,
      },
    },
    dashboard: {
      meta: {
        status: RequestStatus.IDLE,
      },
      data: {
        totalStats: totalStatsDefaultData,
        platformStats: platformStatsDefaultData,
        geoReport: geoReportDefaultData,
      },
      pagination: {
        pageIndex: 0,
        totalPages: 0,
      },
      sorting: {
        orderByField: "",
        orderByDirection: "",
      },
      params: {
        dev: StatisticsPlatform.ALL,
        selectedSiteIds: [],
        from: "",
        groupBy: StatisticsGroupBy.DATE,
        to: "",
        trkid: "",
        lastPayouts: undefined,
      },
    },
  },
};

const statisticsSlice = createSlice({
  name: "statistics",
  initialState,
  reducers: {
    setScrollToTable: (state, action: PayloadAction<boolean>) => {
      state.scrollToTable = action.payload;
    },
    setIsFullscreen: (state, action: PayloadAction<boolean>) => {
      state.isFullscreen = action.payload;
    },

    setActiveTab: (state, action: PayloadAction<StatisticsTab>) => {
      state.tab.activeTab = action.payload;
      state.filter.groupBy = StatisticsGroupBy.DATE;
    },

    setIsReportLoading: (state, action: PayloadAction<boolean>) => {
      state.isReportLoading = action.payload;
    },

    setFields: (state, action: PayloadAction<string[]>) => {
      const activeTab = state.tab.activeTab;
      const fields = current(state.tab.columns[activeTab]);

      state.tab.columns[activeTab] = Object.fromEntries(
        Object.keys(fields).map((key) => [key, action.payload.includes(key)]),
      );
    },

    toggleField: (state, action: PayloadAction<string>) => {
      const activeTab = state.tab.activeTab;
      const field = state.tab.columns[activeTab][action.payload];

      if (field !== undefined) {
        state.tab.columns[activeTab][action.payload] = !field;
      }
    },

    setAllField: (state, action: PayloadAction<boolean>) => {
      const activeTab = state.tab.activeTab;
      const fields = current(state.tab.columns[activeTab]);

      state.tab.columns[activeTab] = Object.fromEntries(Object.keys(fields).map((key) => [key, action.payload]));
    },

    setFilter: (state, action: PayloadAction<Partial<FilterOptions>>) => {
      state.filter = {
        ...state.filter,
        ...action.payload,
      };
    },

    setOpenedFromReportTracker: (state, action: PayloadAction<boolean>) => {
      state.openedFromReportTracker = action.payload;
    },

    setShouldGetReport: (state, action: PayloadAction<boolean>) => {
      state.shouldGetReport = action.payload;
    },

    setDidInit: (state, action: PayloadAction<boolean>) => {
      state.didInit = action.payload;
    },

    setReportCacheKey: (
      state,
      action: PayloadAction<{
        type: ReportType;
        payload?: string;
      }>,
    ) => {
      state.localReports[action.payload.type].cacheKey = action.payload.payload;
    },

    setReportStatus: (
      state,
      action: PayloadAction<{
        type: ReportType;
        payload: RequestStatus;
      }>,
    ) => {
      state.localReports[action.payload.type].meta.status = action.payload.payload;
      state.isReportLoading = action.payload.payload === RequestStatus.PENDING;
    },

    setReport: (
      state,
      action: PayloadAction<{
        type: ReportType;
        status: RequestStatus;
        cacheKey?: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: any;
        params: FilterOptions;
      }>,
    ) => {
      // Set status

      state.localReports[action.payload.type].meta.status = action.payload.status;
      state.isReportLoading = action.payload.status === RequestStatus.PENDING;

      // Set cache key

      state.localReports[action.payload.type].cacheKey = action.payload.cacheKey;

      // Set data

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      state.localReports[action.payload.type].data = action.payload.data;

      // Set params
      state.localReports[action.payload.type].params = action.payload.params;
    },

    setPageIndex: (
      state,
      action: PayloadAction<{
        type: ReportType;
        pageIndex: number;
      }>,
    ) => {
      state.localReports[action.payload.type].pagination.pageIndex = action.payload.pageIndex;
    },

    setTotalPages: (
      state,
      action: PayloadAction<{
        type: ReportType;
        totalPages: number;
      }>,
    ) => {
      state.localReports[action.payload.type].pagination.totalPages = action.payload.totalPages;
    },

    setOrderByField: (
      state,
      action: PayloadAction<{
        type: ReportType;
        field: string;
      }>,
    ) => {
      state.localReports[action.payload.type].sorting.orderByField = action.payload.field;
    },

    setOrderByDirection: (
      state,
      action: PayloadAction<{
        type: ReportType;
        direction: string;
      }>,
    ) => {
      state.localReports[action.payload.type].sorting.orderByDirection = action.payload.direction;
    },

    clearState: () => initialState,
  },
  extraReducers: (builder) => {
    builder

      // Referrer sites
      .addCase(loadSitesAsync.pending, (state) => {
        state.referrerSites.meta.status = RequestStatus.PENDING;
      })

      .addCase(loadSitesAsync.fulfilled, (state, action) => {
        state.referrerSites.meta.status = RequestStatus.SUCCESS;
        state.referrerSites.data.sites = action.payload;

        //this means no data in local storage
        if (state.filter.selectedSiteIds.length < 1) {
          state.filter.selectedSiteIds = action.payload.sortedIds;
        }
      })

      .addCase(loadSitesAsync.rejected, (state) => {
        state.referrerSites.meta.status = RequestStatus.ERROR;
      })

      // Email subscription
      .addCase(getEmailSubscriptionAsync.pending, (state) => {
        state.emailSubscription.meta.initStatus = RequestStatus.PENDING;
      })

      .addCase(getEmailSubscriptionAsync.fulfilled, (state, action) => {
        state.emailSubscription.meta.initStatus = RequestStatus.SUCCESS;
        state.emailSubscription.data = action.payload;
      })

      .addCase(getEmailSubscriptionAsync.rejected, (state) => {
        state.emailSubscription.meta.initStatus = RequestStatus.ERROR;
      })

      .addCase(sendEmailSubscriptionAsync.pending, (state) => {
        state.emailSubscription.meta.status = RequestStatus.PENDING;
      })

      .addCase(sendEmailSubscriptionAsync.fulfilled, (state) => {
        state.emailSubscription.meta.status = RequestStatus.SUCCESS;
      })

      .addCase(sendEmailSubscriptionAsync.rejected, (state) => {
        state.emailSubscription.meta.status = RequestStatus.ERROR;
      })

      .addCase(autoLoadReportAsync.pending, (state) => {
        state.localReports.totalStats.meta.status = RequestStatus.PENDING;
        state.isReportLoading = true;
      })

      .addCase(autoLoadReportAsync.fulfilled, (state, action) => {
        state.localReports.totalStats.meta.status = RequestStatus.SUCCESS;
        // @ts-expect-error This works
        state.localReports.totalStats.data = action.payload;
        state.isReportLoading = false;
      })

      .addCase(autoLoadReportAsync.rejected, (state) => {
        state.localReports.totalStats.meta.status = RequestStatus.ERROR;
        state.isReportLoading = false;
      });
  },
});

export const StatisticsActions = statisticsSlice.actions;

export const statisticsReducer = statisticsSlice.reducer;
